import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import BlendedOfferingsService from "../Service/BlendedOfferingsService";
import BlendedService from "../Service/BlendedService";
import EventCalendar from "../Common/EventCalendar";

function BlendedLearningOfferings() {
  const location = useLocation();
  const { user } = location.state || {};
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedBlendedCourse, setSelectedBlendedCourse] = useState("");
  const [modules, setModules] = useState([]);
  const [availableBlendedCourses, setAvailableBlendedCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addedPrograms, setAddedPrograms] = useState([]);
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [existingOfferings, setExistingOfferings] = useState([]);
  const [loadingExistingOfferings, setLoadingExistingOfferings] = useState(false);
  const [selectedOfferings, setSelectedOfferings] = useState([]);
  const [openingForNominations, setOpeningForNominations] = useState(false);
  const [savingPrograms, setSavingPrograms] = useState(new Set()); // Track which programs are being saved
  const [savingStandalone, setSavingStandalone] = useState(false); // Track standalone save

    // Helper to flatten all connects in all modules with their indices
  // Optimized: Flatten all connects in all modules with their indices
  const getAllConnectsWithIndices = (program) =>
    program.modules.flatMap((mod, mIdx) =>
      mod.connects.map((conn, cIdx) => ({ date: conn.date, moduleIndex: mIdx, connectIndex: cIdx }))
    );

  // Checks for duplicate date in all modules' connects in a program
  // Optimized: Checks for duplicate date in all modules' connects in a program
  const hasDuplicateDateGlobal = (program, moduleIndex, connectIndex, newDate) =>
    !!newDate && getAllConnectsWithIndices(program).some(
      (c) => !(c.moduleIndex === moduleIndex && c.connectIndex === connectIndex) && c.date === newDate
    );

  // Checks if the new date is after the previous connect across all modules (by date order)
  // Optimized: Checks if the new date is after the previous connect across all modules (by date order)
  const isAfterPreviousGlobal = (program, moduleIndex, connectIndex, newDate) => {
    if (!newDate) return true;
    const allConnects = getAllConnectsWithIndices(program)
      .map((c, idx) => ({ ...c, idx }))
      .filter(c => c.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    const currentIdx = allConnects.findIndex(
      (c) => c.moduleIndex === moduleIndex && c.connectIndex === connectIndex
    );
    if (currentIdx > 0 && allConnects[currentIdx - 1]) {
      return new Date(newDate) > new Date(allConnects[currentIdx - 1].date);
    }
    return true;
  };
  
  // Helper functions
  const getDayName = (dateString) =>
    dateString ? ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][new Date(dateString).getDay()] : "";

  // Helper function to check if a date is a weekend
  const isWeekend = (dateString) => {
    if (!dateString) return false;
    const dayOfWeek = new Date(dateString).getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  // Helper function to check for duplicate dates in the same module
  const hasDuplicateDate = (connects, currentIndex, newDate) =>
    connects.some((connect, index) => index !== currentIndex && connect.date === newDate);

  // Helper function to validate chronological order
  const isChronologicalOrder = (connects, currentIndex, newDate) =>
    !newDate || (currentIndex === 0 || (connects[currentIndex - 1] && connects[currentIndex - 1].date ? new Date(newDate) > new Date(connects[currentIndex - 1].date) : true));

  const getMinimumDate = () => {
    if (!selectedMonth) return new Date().toISOString().split('T')[0];
    const today = new Date();
    const selectedDate = new Date(selectedMonth + '-01');
    if (selectedDate.getMonth() === today.getMonth() && selectedDate.getFullYear() === today.getFullYear()) {
      return today.toISOString().split('T')[0];
    }
    return selectedDate > today ? selectedDate.toISOString().split('T')[0] : today.toISOString().split('T')[0];
  };

  const validateProgram = (program) => {
    const errors = [];
    if (!selectedMonth) errors.push("Please select a month");
    program.modules.forEach((module, moduleIndex) => {
      // Validate SME Connects
      module.connects.forEach((connect, connectIndex) => {
        const connectLabel = `Module "${module.moduleName}" Connect ${connectIndex + 1}`;
        if (!connect.date) errors.push(`${connectLabel} date is required`);
        if (!connect.time) errors.push(`${connectLabel} time is required`);
       // if (!connect.day) errors.push(`${connectLabel} day is required`);
      });

      // Find last SME Connect date (latest date among connects)
      const connectDates = module.connects
        .map((c) => c.date)
        .filter(Boolean)
        .map((d) => new Date(d));
      let lastConnectDate = null;
      if (connectDates.length > 0) {
        lastConnectDate = new Date(Math.max(...connectDates.map((d) => d.getTime())));
      }

      // Validate assessmentDate and quizDate are after lastConnectDate
      if (lastConnectDate) {
        if (module.assessmentDate) {
          const assessmentDateObj = new Date(module.assessmentDate);
          if (assessmentDateObj <= lastConnectDate) {
            errors.push(
              `Module "${module.moduleName}" Assessment date (${module.assessmentDate}) must be after last SME Connect date (${lastConnectDate.toISOString().split('T')[0]})`
            );
          }
        }
        if (module.quizDate) {
          const quizDateObj = new Date(module.quizDate);
          if (quizDateObj <= lastConnectDate) {
            errors.push(
              `Module "${module.moduleName}" Quiz date (${module.quizDate}) must be after last SME Connect date (${lastConnectDate.toISOString().split('T')[0]})`
            );
          }
        }
      }
    });
    return errors;
  };

  // Fetch functions
  useEffect(() => {
    const fetchBlendedCourses = async () => {
      if (!selectedMonth) {
        setAvailableBlendedCourses([]);
        return;
      }
      
      try {
        setLoading(true);
        const blendedCourses = await BlendedService.getAllBlendedCourses(user.track);
        setAvailableBlendedCourses(blendedCourses);
      } catch (error) {
        console.error("Error fetching blended courses:", error);
        alert("Failed to load blended courses: " + error.message);
        setAvailableBlendedCourses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBlendedCourses();
  }, [selectedMonth, user.track]);

  useEffect(() => {
    const fetchExistingOfferings = async () => {
      if (!selectedMonth || !user?.track) {
        setExistingOfferings([]);
        return;
      }
      try {
        setLoadingExistingOfferings(true);
        const blendedOfferings = await BlendedOfferingsService.getProgramsByTrackAndMonth(user.track, selectedMonth);
        const transformedExistingOfferings = blendedOfferings
          .filter(offering => offering.blendedProgramName)
          .map((program, index) => ({
            id: `existing-${program.blendedProgramOfferingId || index}`,
            blendedProgramName: program.blendedProgramName,
            blendedProgramId: program.blendedProgramOfferingId,
            programWDCourseCode: program.programWDCourseCode,
            track: program.track,
            publishedDate: program.publishedDate,
            publishedBy: program.publishedBy,
            status: program.status,
            openForNomination: program.openForNomination || false,
            modules: program.modules ? program.modules.map((module) => ({
              ...module,
              connects: module.connects ? module.connects.map((connect) => ({
                date: connect.date || "",
                time: connect.time || "",
                day: connect.day || "",
              })) : Array.from({ length: module.numOfSMEConnects || 1 }, () => ({
                date: "",
                time: "",
                day: "",
              })),
              assessmentDate: module.assessmentDate || "",
              quizDate: module.quizDate || "",
            })) : [],
            isEditing: false,
            isExisting: true
          }));
        setExistingOfferings(transformedExistingOfferings);
        
        // Remove any programs from addedPrograms that already exist in the backend
        const backendProgramIds = blendedOfferings.map(program => program.blendedProgramOfferingId).filter(Boolean);
        setAddedPrograms(prev => 
          prev.filter(program => !backendProgramIds.includes(program.blendedProgramId))
        );
      } catch (error) {
        console.error("Error fetching existing offerings:", error);
        setExistingOfferings([]);
      } finally {
        setLoadingExistingOfferings(false);
      }
    };
    fetchExistingOfferings();
  }, [selectedMonth, user?.track]);

  useEffect(() => {
    const fetchModules = async () => {
      if (!selectedBlendedCourse) {
        setModules([]);
        return;
      }
      try {
        setLoading(true);
        const selectedCourseObj = availableBlendedCourses.find(
          course => course.blendedProgramName === selectedBlendedCourse
        );
        let courseModules;
        if (selectedCourseObj && selectedCourseObj.modules) {
          courseModules = selectedCourseObj.modules;
        } else {
          courseModules = await BlendedOfferingsService.getCourseModulesByProgram(selectedBlendedCourse);
        }
        const transformed = courseModules.map((module) => ({
          ...module,
          connects: Array.from({ length: module.numOfSMEConnects || 1 }, () => ({
            date: "",
            time: "",
            day: "",
          })),
          assessmentDate: "",
          quizDate: "",
        }));
        setModules(transformed);
      } catch (error) {
        console.error("Error fetching modules:", error);
        alert("Failed to load course modules: " + error.message);
        setModules([]);
      } finally {
        setLoading(false);
      }
    };
    fetchModules();
  }, [selectedBlendedCourse, availableBlendedCourses]);

  // Main handlers
  const handleAddProgram = async () => {
    if (!selectedBlendedCourse) {
      alert("Please select a blended program first");
      return;
    }
    try {
      setLoading(true);
      const selectedCourseObj = availableBlendedCourses.find(
        course => course.blendedProgramName === selectedBlendedCourse
      );
      let courseModules;
      if (selectedCourseObj && selectedCourseObj.modules) {
        courseModules = selectedCourseObj.modules;
      } else {
        courseModules = await BlendedOfferingsService.getCourseModulesByProgram(selectedBlendedCourse);
      }
      const transformedModules = courseModules.map((module) => ({
        ...module,
        connects: Array.from({ length: module.numOfSMEConnects || 1 }, () => ({
          date: "",
          time: "",
          day: "",
        })),
        assessmentDate: "",
        quizDate: "",
      }));

      const newProgram = {
        id: Date.now(),
        blendedProgramName: selectedBlendedCourse,
        blendedProgramId: selectedCourseObj?.blendedProgramId || `program-${Date.now()}`,
        programWDCourseCode: selectedCourseObj?.programWDCourseCode || "",
        track: selectedCourseObj?.track || user.track,
        modules: transformedModules,
        isEditing: false,
        openForNomination: false
      };

      setAddedPrograms(prev => [...prev, newProgram]);
      setSelectedBlendedCourse("");
    } catch (error) {
      console.error("Error adding program:", error);
      alert("Failed to add program: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleProgramExpansion = (programId) => {
    setExpandedPrograms(prev => ({
      ...prev,
      [programId]: !prev[programId]
    }));
  };

  const handleRemoveProgram = async (blendedProgramOfferingId) => {
    let program = null;
    let isInExistingOfferings = false;
    let hasDatabaseId = false;

    if (blendedProgramOfferingId.toString().startsWith('existing-')) {
      program = existingOfferings.find(p => p.id === blendedProgramOfferingId);
      isInExistingOfferings = true;
      hasDatabaseId = true;
    } else {
      program = addedPrograms.find(p => p.id === blendedProgramOfferingId);
      if (program && program.savedToDatabase) {
        hasDatabaseId = true;
      }
    }

    if (!program) {
      alert("Program not found");
      return;
    }

    if (hasDatabaseId) {
      const confirmDelete = window.confirm(
        `Are you sure you want to permanently delete the offering "${program.blendedProgramName}" from the database? \n This action cannot be undone.`
      );
      if (!confirmDelete) return;

      try {
        setLoading(true);
        const databaseId = program.blendedProgramId || program.id.toString().replace('existing-', '');
        await BlendedOfferingsService.deleteExistingOffering(databaseId);
        
        if (isInExistingOfferings) {
          setExistingOfferings(prev => prev.filter(p => p.id !== blendedProgramOfferingId));
        } else {
          setAddedPrograms(prev => prev.filter(p => p.id !== blendedProgramOfferingId));
        }
        
        setExpandedPrograms(prev => {
          const updated = { ...prev };
          delete updated[blendedProgramOfferingId];
          return updated;
        });
        
        alert(`Offering "${program.blendedProgramName}" has been successfully deleted from the database.`);
      } catch (error) {
        console.error("Error deleting offering:", error);
        alert("Failed to delete offering: " + error.message);
      } finally {
        setLoading(false);
      }
    } else {
      setAddedPrograms(prev => prev.filter(program => program.id !== blendedProgramOfferingId));
      setExpandedPrograms(prev => {
        const updated = { ...prev };
        delete updated[blendedProgramOfferingId];
        return updated;
      });
    }
  };

  const handleEditProgram = (programId) => {
    if (programId.toString().startsWith('existing-')) {
      setExistingOfferings(prev =>
        prev.map(program =>
          program.id === programId
            ? { ...program, isEditing: !program.isEditing }
            : program
        )
      );
    } else {
      setAddedPrograms(prev =>
        prev.map(program =>
          program.id === programId
            ? { ...program, isEditing: !program.isEditing }
            : program
        )
      );
    }
  };

  const handleSaveProgram = async (programId) => {
    // Prevent duplicate saves
    if (savingPrograms.has(programId)) {
      return;
    }

    let program = addedPrograms.find(p => p.id === programId);
    let isExistingOffering = false;
    
    if (!program) {
      program = existingOfferings.find(p => p.id === programId);
      isExistingOffering = true;
    }
    
    if (!program) {
      alert("Program not found");
      return;
    }

    const validationErrors = validateProgram(program);
    if (validationErrors.length > 0) {
      const errorMessage = "Please fix the following issues:\n\n" + 
        validationErrors.map((error, index) => `${index + 1}. ${error}`).join("\n");
      alert(errorMessage);
      return;
    }

    // Optimize payload construction - only include modules with actual data
    const optimizedModules = program.modules
      .filter(module => module.moduleName || module.connects.some(c => c.date || c.time))
      .map((module) => {
        const optimizedModule = {
          courseName: program.blendedProgramName,
          numOfSMEConnects: module.numOfSMEConnects || module.connects.length,
          quiz: module.quiz || false
        };
        
        // Only add non-empty values
        if (module.moduleId) optimizedModule.moduleId = module.moduleId;
        if (module.moduleName) optimizedModule.moduleName = module.moduleName;
        if (module.moduleWDCourseCode) optimizedModule.moduleWDCourseCode = module.moduleWDCourseCode;
        if (module.description) optimizedModule.description = module.description;
        if (module.duration) optimizedModule.duration = module.duration;
        if (module.assessmentDate) optimizedModule.assessmentDate = module.assessmentDate;
        if (module.quizDate) optimizedModule.quizDate = module.quizDate;
        
        optimizedModule.connects = module.connects
          .filter(connect => connect.date || connect.time)
          .map((connect) => {
            const optimizedConnect = {};
            if (connect.date) optimizedConnect.date = connect.date;
            if (connect.time) optimizedConnect.time = connect.time;
            if (connect.day) optimizedConnect.day = connect.day;
            return optimizedConnect;
          });
          
        return optimizedModule;
      });

    const payload = {
      month: selectedMonth,
      program: program.blendedProgramName,
      blendedProgramName: program.blendedProgramName,
      track: program.track || user.track,
      modules: optimizedModules,
      publishedDate: isExistingOffering ? (program.publishedDate || new Date().toISOString().split('T')[0]) : new Date().toISOString().split('T')[0],
      publishedBy: isExistingOffering ? (program.publishedBy || user.name) : user.name,
      status: program.status || "PUBLISHED"
    };
    
    // Conditionally add optional fields
    if (program.programWDCourseCode) {
      payload.programWDCourseCode = program.programWDCourseCode;
    }
    if (isExistingOffering && program.blendedProgramId) {
      payload.blendedProgramOfferingId = program.blendedProgramId;
    }

    try {
      // Set loading state for this specific program
      setSavingPrograms(prev => new Set([...prev, programId]));
      setLoading(true);
      
      console.log("Optimized Program Payload:", payload);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      try {
        const result = await BlendedOfferingsService.saveBlendedOffering(payload);
        clearTimeout(timeoutId);
        
        alert(`Program "${program.blendedProgramName}" ${isExistingOffering ? 'updated' : 'saved'} successfully!`);
        
        // Optimize state updates with batching
        if (isExistingOffering) {
          setExistingOfferings(prev =>
            prev.map(p =>
              p.id === programId
                ? { ...p, isEditing: false }
                : p
            )
          );
        } else {
          // Use setTimeout to batch state updates
          setTimeout(() => {
            const savedProgram = {
              ...program,
              id: `existing-${result.blendedProgramOfferingId}`,
              blendedProgramId: result.blendedProgramOfferingId,
              isEditing: false,
              isExisting: true,
              savedToDatabase: true
            };
            
            setAddedPrograms(prev => prev.filter(p => p.id !== programId));
            setExistingOfferings(prev => [...prev, savedProgram]);
          }, 0);
        }
      } catch (timeoutError) {
        clearTimeout(timeoutId);
        if (timeoutError.name === 'AbortError') {
          throw new Error("Save operation timed out. Please try again.");
        }
        throw timeoutError;
      }
    } catch (error) {
      console.error("Error saving program:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to save program";
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
      setSavingPrograms(prev => {
        const updated = new Set(prev);
        updated.delete(programId);
        return updated;
      });
    }
  };

  // Simplified module update function
  const updateProgramModule = (programId, moduleIndex, updatedModule) => {
    if (programId.toString().startsWith('existing-')) {
      setExistingOfferings(prev => 
        prev.map(program => 
          program.id === programId 
            ? {
                ...program,
                modules: program.modules.map((mod, idx) => 
                  idx === moduleIndex ? updatedModule : mod
                )
              }
            : program
        )
      );
    } else {
      setAddedPrograms(prev => 
        prev.map(program => 
          program.id === programId 
            ? {
                ...program,
                modules: program.modules.map((mod, idx) => 
                  idx === moduleIndex ? updatedModule : mod
                )
              }
            : program
        )
      );
    }
  };

  const handleProgramConnectChange = (programId, moduleIndex, connectIndex, field, value) => {
    let program = addedPrograms.find(p => p.id === programId);
    if (!program) {
      program = existingOfferings.find(p => p.id === programId);
    }
    if (!program) return;

    if (field === "date" && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Check if date is in the past
      if (selectedDate < today) {
        alert("Connect date cannot be in the past. Please select a future date.");
        return;
      }
      
      // Check if it's a weekend (only if value is set and not empty)
      if (value && isWeekend(value)) {
        alert("SME Connect sessions cannot be scheduled on weekends (Saturday/Sunday). Please select a weekday.");
        return;
      }
      
      // Check for duplicate dates in the same module
      if (hasDuplicateDate(program.modules[moduleIndex].connects, connectIndex, value)) {
        alert("This date is already selected for another SME Connect in this module. Please choose a different date.");
        return;
      }
      
      // Check for duplicate dates across all modules
      if (hasDuplicateDateGlobal(program, moduleIndex, connectIndex, value)) {
        alert("This date is already selected for another SME Connect in this program (across all modules). Please choose a different date.");
        return;
      }
      
      // Enhanced validation: Ensure the new date is after the last SME connect date in the same module
      const currentModuleConnects = program.modules[moduleIndex].connects
        .map((c, cIdx) => ({ date: c.date, index: cIdx }))
        .filter(c => c.index !== connectIndex && c.date); // Exclude current connect and empty dates
      
      if (currentModuleConnects.length > 0) {
        const latestModuleConnect = currentModuleConnects.reduce((latest, current) => 
          !latest || new Date(current.date) > new Date(latest.date) ? current : latest
        );
        
        if (new Date(value) <= new Date(latestModuleConnect.date)) {
          alert(`New SME Connect date must be after the last SME Connect date (${latestModuleConnect.date}) in this module. Please select a date after ${latestModuleConnect.date}.`);
          return;
        }
      }
      
      // Check chronological order across all modules (existing validation)
      if (!isAfterPreviousGlobal(program, moduleIndex, connectIndex, value)) {
        alert("SME Connect date must be after the previous connect date (across all modules). Please select a valid date.");
        return;
      }
    }

    const updatedModule = {
      ...program.modules[moduleIndex],
      connects: program.modules[moduleIndex].connects.map((conn, cIdx) =>
        cIdx === connectIndex 
          ? { 
              ...conn, 
              [field]: value,
              ...(field === "date" && { day: getDayName(value) })
            } 
          : conn
      )
    };

    updateProgramModule(programId, moduleIndex, updatedModule);
  };

  const handleProgramAddConnect = (programId, moduleIndex) => {
    let program = addedPrograms.find(p => p.id === programId);
    if (!program) {
      program = existingOfferings.find(p => p.id === programId);
    }
    if (!program) return;

    const updatedModule = {
      ...program.modules[moduleIndex],
      connects: [...program.modules[moduleIndex].connects, { 
        date: "", 
        time: "", 
        day: "" 
      }]
    };

    updateProgramModule(programId, moduleIndex, updatedModule);
  };

  const handleProgramRemoveConnect = (programId, moduleIndex, connectIndex) => {
    let program = addedPrograms.find(p => p.id === programId);
    if (!program) {
      program = existingOfferings.find(p => p.id === programId);
    }
    if (!program || program.modules[moduleIndex].connects.length <= 1) return;

    const updatedModule = {
      ...program.modules[moduleIndex],
      connects: program.modules[moduleIndex].connects.filter((_, cIdx) => cIdx !== connectIndex)
    };

    updateProgramModule(programId, moduleIndex, updatedModule);
  };

  const handleProgramAssessmentDateChange = (programId, moduleIndex, value) => {
    let program = addedPrograms.find(p => p.id === programId);
    if (!program) {
      program = existingOfferings.find(p => p.id === programId);
    }
    if (!program) return;

    if (value) {
      const assessmentDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (assessmentDate < today) {
        alert("Assessment date cannot be in the past. Please select a future date.");
        return;
      }
      if (isWeekend(value)) {
        alert("Assessment date cannot be a weekend (Saturday/Sunday). Please select a weekday.");
        return;
      }
    }

    const updatedModule = {
      ...program.modules[moduleIndex],
      assessmentDate: value
    };

    updateProgramModule(programId, moduleIndex, updatedModule);
  };

  const handleProgramQuizDateChange = (programId, moduleIndex, value) => {
    let program = addedPrograms.find(p => p.id === programId);
    if (!program) {
      program = existingOfferings.find(p => p.id === programId);
    }
    if (!program) return;

    if (value) {
      const quizDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (quizDate < today) {
        alert("Quiz date cannot be in the past. Please select a future date.");
        return;
      }
      if (isWeekend(value)) {
        alert("Quiz date cannot be a weekend (Saturday/Sunday). Please select a weekday.");
        return;
      }
    }

    const updatedModule = {
      ...program.modules[moduleIndex],
      quizDate: value
    };

    updateProgramModule(programId, moduleIndex, updatedModule);
  };

  // Simple connect handlers for standalone modules
  const handleConnectChange = (moduleIndex, connectIndex, field, value) => {
    if (field === "date" && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        alert("Connect date cannot be in the past. Please select a future date.");
        return;
      }
    }

    setModules((prev) =>
      prev.map((mod, idx) =>
        idx === moduleIndex
          ? {
              ...mod,
              connects: mod.connects.map((conn, cIdx) =>
                cIdx === connectIndex 
                  ? { 
                      ...conn, 
                      [field]: value,
                      ...(field === "date" && { day: getDayName(value) })
                    } 
                  : conn
              ),
            }
          : mod
      )
    );
  };

  const handleAddConnect = (moduleIndex) => {
    setModules((prev) =>
      prev.map((mod, idx) =>
        idx === moduleIndex
          ? { ...mod, connects: [...mod.connects, { date: "", time: "", day: "" }] }
          : mod
      )
    );
  };

  const handleRemoveConnect = (moduleIndex, connectIndex) => {
    setModules((prev) =>
      prev.map((mod, idx) =>
        idx === moduleIndex && mod.connects.length > 1
          ? { ...mod, connects: mod.connects.filter((_, cIdx) => cIdx !== connectIndex) }
          : mod
      )
    );
  };

  const handleSave = async () => {
    if (!selectedMonth || !selectedBlendedCourse) {
      alert("Please select both month and blended course");
      return;
    }

    // Prevent duplicate saves
    if (savingStandalone) {
      return;
    }

    const selectedCourseObj = availableBlendedCourses.find(
      course => course.blendedProgramName === selectedBlendedCourse
    );

    // Optimize payload - only include non-null/non-empty values
    const optimizedModules = modules
      .filter(module => module.moduleName || module.connects.some(c => c.date || c.time))
      .map((module) => {
        const optimizedModule = {
          courseName: selectedBlendedCourse,
          numOfSMEConnects: module.numOfSMEConnects || module.connects.length,
          quiz: module.quiz || false
        };
        
        // Only add non-null values
        if (module.moduleId) optimizedModule.moduleId = module.moduleId;
        if (module.moduleName) optimizedModule.moduleName = module.moduleName;
        if (module.moduleWDCourseCode) optimizedModule.moduleWDCourseCode = module.moduleWDCourseCode;
        if (module.description) optimizedModule.description = module.description;
        if (module.duration) optimizedModule.duration = module.duration;
        if (module.assessmentDate) optimizedModule.assessmentDate = module.assessmentDate;
        if (module.quizDate) optimizedModule.quizDate = module.quizDate;
        
        optimizedModule.connects = module.connects
          .filter(connect => connect.date || connect.time) // Only include connects with data
          .map((connect) => {
            const optimizedConnect = {};
            if (connect.date) optimizedConnect.date = connect.date;
            if (connect.time) optimizedConnect.time = connect.time;
            if (connect.day) optimizedConnect.day = connect.day;
            return optimizedConnect;
          });
          
        return optimizedModule;
      });

    const payload = {
      month: selectedMonth,
      program: selectedBlendedCourse,
      blendedProgramName: selectedCourseObj?.blendedProgramName || selectedBlendedCourse,
      modules: optimizedModules,
      publishedDate: new Date().toISOString().split('T')[0],
      publishedBy: user.name,
      status: "PUBLISHED"
    };
    
    // Only add non-null values
    if (selectedCourseObj?.programWDCourseCode) {
      payload.programWDCourseCode = selectedCourseObj.programWDCourseCode;
    }

    try {
      setSavingStandalone(true);
      setLoading(true);
      
      console.log("Optimized Standalone Payload:", payload);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      try {
        const result = await BlendedOfferingsService.saveBlendedOffering(payload);
        clearTimeout(timeoutId);
        
        alert("Course published successfully!");
        setSelectedMonth("");
        setSelectedBlendedCourse("");
        setModules([]);
      } catch (timeoutError) {
        clearTimeout(timeoutId);
        if (timeoutError.name === 'AbortError') {
          throw new Error("Save operation timed out. Please try again.");
        }
        throw timeoutError;
      }
    } catch (error) {
      console.error("Error publishing course:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to publish course";
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
      setSavingStandalone(false);
    }
  };

  // Selection handlers
  const handleOfferingSelection = (offeringId, isSelected) => {
    // Find the program to check its status
    const allPrograms = [...existingOfferings, ...addedPrograms];
    const program = allPrograms.find(p => p.id === offeringId);
    
    // Don't allow selection if program is finalized, already open for nominations, or is a new program
    const isNewProgram = addedPrograms.some(p => p.id === offeringId);
    if (program && (program.status === 'FINALIZED' || program.openForNomination || isNewProgram)) {
      return;
    }
    
    if (isSelected) {
      setSelectedOfferings(prev => [...prev, offeringId]);
    } else {
      setSelectedOfferings(prev => prev.filter(id => id !== offeringId));
    }
  };

  const handleSelectAll = () => {
    // Only select existing programs that are not finalized and not already open for nominations
    const selectableOfferingIds = existingOfferings
      .filter(program => !(program.status === 'FINALIZED' || program.openForNomination))
      .map(program => program.id);
    setSelectedOfferings(selectableOfferingIds);
  };

  const handleDeselectAll = () => {
    setSelectedOfferings([]);
  };

  const handleOpenForNominations = async () => {
    if (selectedOfferings.length === 0) {
      alert("Please select at least one offering to open for nominations");
      return;
    }

    if (!selectedMonth) {
      alert("Please select a month first");
      return;
    }

    if (!user.track) {
      alert("User track information is missing");
      return;
    }

    // Prevent multiple simultaneous operations
    if (openingForNominations) {
      return;
    }

    // Get the selected offering details
    const allPrograms = [...existingOfferings, ...addedPrograms];
    const selectedPrograms = allPrograms.filter(program => selectedOfferings.includes(program.id));
    
    // Optimize payload - only send essential data, not full module objects
    const optimizedOfferingBeans = selectedPrograms.map(program => ({
      blendedProgramOfferingId: program.blendedProgramId,
      month: selectedMonth,
      blendedProgramName: program.blendedProgramName,
      track: program.track || user.track,
      status: program.status || "PUBLISHED",
      // Only include non-empty optional fields
      ...(program.programWDCourseCode && { programWDCourseCode: program.programWDCourseCode }),
      ...(program.publishedDate && { publishedDate: program.publishedDate }),
      ...(program.publishedBy && { publishedBy: program.publishedBy }),
      // Send minimal module data - just IDs and essential info
      modules: program.modules.map(module => ({
        ...(module.moduleId && { moduleId: module.moduleId }),
        moduleName: module.moduleName,
        numOfSMEConnects: module.numOfSMEConnects || module.connects?.length || 1
      }))
    }));

    try {
      setOpeningForNominations(true);
      
      console.log("Optimized Open Nominations Payload:", optimizedOfferingBeans);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 45000); // 45 second timeout for this operation
      
      try {
        await BlendedOfferingsService.openNominationsBlendedCourses(
          selectedMonth,
          user.track,
          optimizedOfferingBeans
        );
        clearTimeout(timeoutId);
        
        // Extract program names for display
        const programNames = selectedPrograms.map(program => program.blendedProgramName).join('\n• ');
        
        // Clear selections immediately for better UX
        setSelectedOfferings([]);
        
        // Show success message first
        alert(`Successfully opened offering(s) for nominations!\n\nPrograms opened for nominations:\n• ${programNames}`);
        
        // Optimize refresh - use minimal update instead of full refresh
        setExistingOfferings(prev => 
          prev.map(program => {
            if (selectedOfferings.includes(program.id)) {
              return { ...program, openForNomination: true };
            }
            return program;
          })
        );
        
        // Debounced full refresh in background
        setTimeout(async () => {
          try {
            const blendedOfferings = await BlendedOfferingsService.getProgramsByTrackAndMonth(user.track, selectedMonth);
            const transformedExistingOfferings = blendedOfferings
              .filter(offering => offering.blendedProgramName)
              .map((program, index) => ({
                id: `existing-${program.blendedProgramOfferingId || index}`,
                blendedProgramName: program.blendedProgramName,
                blendedProgramId: program.blendedProgramOfferingId,
                programWDCourseCode: program.programWDCourseCode,
                track: program.track,
                publishedDate: program.publishedDate,
                publishedBy: program.publishedBy,
                status: program.status,
                openForNomination: program.openForNomination || false,
                modules: program.modules ? program.modules.map((module) => ({
                  ...module,
                  moduleId: module.moduleId || "",
                  connects: module.connects ? module.connects.map((connect) => ({
                    date: connect.date || "",
                    time: connect.time || "",
                    day: connect.day || "",
                  })) : Array.from({ length: module.numOfSMEConnects || 1 }, () => ({
                    date: "",
                    time: "",
                    day: "",
                  })),
                  assessmentDate: module.assessmentDate || "",
                  quizDate: module.quizDate || "",
                })) : [],
                isEditing: false,
                isExisting: true
              }));
            setExistingOfferings(transformedExistingOfferings);
            
            // Remove any programs from addedPrograms that now exist in the backend
            const backendProgramIds = blendedOfferings.map(program => program.blendedProgramOfferingId).filter(Boolean);
            setAddedPrograms(prev => 
              prev.filter(program => !backendProgramIds.includes(program.blendedProgramId))
            );
          } catch (refreshError) {
            console.error("Background refresh failed:", refreshError);
            // Don't show error to user since main operation succeeded
          }
        }, 500); // Delayed background refresh
        
      } catch (timeoutError) {
        clearTimeout(timeoutId);
        if (timeoutError.name === 'AbortError') {
          throw new Error("Operation timed out. Please try again.");
        }
        throw timeoutError;
      }
    } catch (error) {
      console.error("Error opening for nominations:", error);
      const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred";
      alert(`Failed to open for nominations: ${errorMessage}`);
    } finally {
      setOpeningForNominations(false);
    }
  };

  // Render program table rows (simplified)
  const renderProgramTable = (program) => (
    <div className="px-6 pb-6">
      <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[1400px] table-fixed">
            <thead>
              <tr className="bg-gray-200 text-gray-600">
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-48">Module</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-32">Code</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-64">Description</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-24">Duration</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-40">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-48">Time</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-32">Day</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-40">Quiz</th>
                <th className="px-4 py-3 text-left font-semibold text-sm border-b-2 w-40">Assessment</th>
                <th className="px-4 py-3 text-center font-semibold text-sm border-b-2 w-24">Action</th>
              </tr>
            </thead>
            <tbody>
              {program.modules.map((mod, moduleIndex) =>
                mod.connects.map((connect, connectIndex) => (
                  <tr 
                    key={`${moduleIndex}-${connectIndex}`} 
                    className="hover:bg-gray-50 border-b border-gray-200"
                  >
                    {connectIndex === 0 && (
                      <>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 border-r border-gray-200 text-sm font-medium align-top">
                          <div className="mb-2">{mod.moduleName}</div>
                          <button
                            className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded hover:bg-green-600 transition-colors disabled:bg-gray-400"
                            onClick={() => handleProgramAddConnect(program.id, moduleIndex)}
                            disabled={!program.isEditing}
                          >
                            Add Connect
                          </button>
                        </td>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 border-r border-gray-200 text-sm align-top">
                          {mod.moduleWDCourseCode || 'N/A'}
                        </td>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 border-r border-gray-200 text-sm align-top">
                          {mod.description}
                        </td>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 border-r border-gray-200 text-sm text-center font-semibold text-gray-600 align-top">
                          {mod.duration}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 border-r border-gray-200 text-sm">
                      <input
                        type="date"
                        value={connect.date}
                        min={(() => {
                          // For new connects in the same module, ensure they are after the last SME connect date of that module
                          const currentModuleConnects = program.modules[moduleIndex].connects
                            .map((c, cIdx) => ({ date: c.date, index: cIdx }))
                            .filter(c => c.index !== connectIndex && c.date); // Exclude current connect and empty dates
                          
                          if (currentModuleConnects.length > 0) {
                            // Find the latest connect date in the same module
                            const latestModuleConnect = currentModuleConnects.reduce((latest, current) => 
                              !latest || new Date(current.date) > new Date(latest.date) ? current : latest
                            );
                            
                            // Set minimum date to day after the last connect date in the same module
                            const nextDay = new Date(latestModuleConnect.date);
                            nextDay.setDate(nextDay.getDate() + 1);
                            return nextDay.toISOString().split('T')[0];
                          }
                          
                          // If no previous connects in the same module, find the latest connect across all other modules
                          const allOtherConnects = [];
                          program.modules.forEach((m, mIdx) => {
                            if (mIdx !== moduleIndex) { // Only consider other modules
                              m.connects.forEach((c, cIdx) => {
                                if (c.date) {
                                  allOtherConnects.push({ date: c.date });
                                }
                              });
                            }
                          });
                          
                          if (allOtherConnects.length > 0) {
                            const latestOtherConnect = allOtherConnects.reduce((latest, current) => 
                              !latest || new Date(current.date) > new Date(latest.date) ? current : latest
                            );
                            const nextDay = new Date(latestOtherConnect.date);
                            nextDay.setDate(nextDay.getDate() + 1);
                            return nextDay.toISOString().split('T')[0];
                          }
                          
                          return getMinimumDate();
                        })()}
                        onChange={(e) =>
                          handleProgramConnectChange(
                            program.id,
                            moduleIndex,
                            connectIndex,
                            "date",
                            e.target.value
                          )
                        }
                        disabled={!program.isEditing}
                        className={`w-full px-3 py-2 border border-gray-300 rounded text-xs ${
                          !program.isEditing ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      />
                    </td>
                    <td className="px-4 py-3 border-r border-gray-200 text-sm">
                      <input
                        type="text"
                        value={connect.time}
                        onChange={(e) =>
                          handleProgramConnectChange(
                            program.id,
                            moduleIndex,
                            connectIndex,
                            "time",
                            e.target.value
                          )
                        }
                        placeholder="9:00 AM to 11:00 AM"
                        disabled={!program.isEditing}
                        className={`w-full px-3 py-2 border border-gray-300 rounded text-xs ${
                          !program.isEditing ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      />
                    </td>
                    <td className="px-4 py-3 border-r border-gray-200 text-sm">
                      <input
                        type="text"
                        value={connect.day}
                        readOnly
                        className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded text-xs cursor-not-allowed text-gray-600"
                      />
                    </td>
                    {connectIndex === 0 && (
                      <>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 border-r border-gray-200 text-sm align-top">
                          <input
                            type="date"
                            value={mod.quizDate}
                            min={(() => {
                              // Find last SME Connect date for this module
                              const connectDates = mod.connects
                                .map((c) => c.date)
                                .filter(Boolean)
                                .map((d) => new Date(d));
                              if (connectDates.length > 0) {
                                const lastConnect = new Date(Math.max(...connectDates.map((d) => d.getTime())));
                                lastConnect.setDate(lastConnect.getDate() + 1);
                                return lastConnect.toISOString().split('T')[0];
                              }
                              return getMinimumDate();
                            })()}
                            onChange={(e) =>
                              handleProgramQuizDateChange(
                                program.id,
                                moduleIndex,
                                e.target.value
                              )
                            }
                            disabled={!program.isEditing}
                            className={`w-full px-3 py-2 border border-gray-300 rounded text-xs ${
                              !program.isEditing ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                          />
                        </td>
                        <td rowSpan={mod.connects.length} className="px-4 py-3 text-sm align-top">
                          <input
                            type="date"
                            value={mod.assessmentDate}
                            min={(() => {
                              // Find last SME Connect date for this module
                              const connectDates = mod.connects
                                .map((c) => c.date)
                                .filter(Boolean)
                                .map((d) => new Date(d));
                              if (connectDates.length > 0) {
                                const lastConnect = new Date(Math.max(...connectDates.map((d) => d.getTime())));
                                lastConnect.setDate(lastConnect.getDate() + 1);
                                return lastConnect.toISOString().split('T')[0];
                              }
                              return getMinimumDate();
                            })()}
                            onChange={(e) =>
                              handleProgramAssessmentDateChange(
                                program.id,
                                moduleIndex,
                                e.target.value
                              )
                            }
                            disabled={!program.isEditing}
                            className={`w-full px-3 py-2 border border-gray-300 rounded text-xs ${
                              !program.isEditing ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                          />
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-center">
                      {mod.connects.length > 1 && program.isEditing && (
                        <button
                          className="px-2 py-1 bg-red-500 text-white text-xs font-semibold rounded hover:bg-red-600 transition-colors"
                          onClick={() => handleProgramRemoveConnect(program.id, moduleIndex, connectIndex)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Blended Learning Offerings</h2>
      
      <div className="flex space-x-6 mb-8">
        <div className="w-1/2">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-300">
            <div className="mb-6">
              <label htmlFor="month" className="block text-gray-700 text-sm font-semibold mb-2">
                Select Month
              </label>
              <input
                id="month"
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="blendedCourse" className="block text-gray-700 text-sm font-semibold mb-2">
                Blended Program
              </label>
              <div className="flex space-x-3">
                <select
                  id="blendedCourse"
                  value={selectedBlendedCourse}
                  onChange={(e) => setSelectedBlendedCourse(e.target.value)}
                  disabled={loading}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:bg-gray-100"
                >
                  <option value="">
                    {!selectedMonth ? "--Select Month First--" :
                     loading ? "--Loading Programs--" : 
                     availableBlendedCourses.length === 0 ? "--No Programs Available--" : 
                     "--Select Program--"}
                  </option>
                  {availableBlendedCourses.map((course, index) => (
                    <option key={course.blendedProgramId || `course-${index}`} value={course.blendedProgramName}>
                      {course.blendedProgramName}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddProgram}
                  disabled={!selectedBlendedCourse || loading}
                  className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 disabled:bg-gray-400 transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="w-1/2">
          <div className="bg-gray-100 rounded-lg shadow-lg p-4 h-full">
            <h3 className="text-lg font-bold text-gray-700 mb-4 text-center">Events Calendar</h3>
            <EventCalendar selectedMonth={selectedMonth} />
          </div>
        </div>
      </div>

      {(existingOfferings.length > 0 || addedPrograms.length > 0) && (
        <div className="bg-white rounded-lg shadow-md border border-gray-300 overflow-hidden mb-8">
          <div className="bg-blue-500 text-white px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg font-semibold">
              Blended Learning Programs for {selectedMonth ? new Date(selectedMonth).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Selected Month'}
            </h3>
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                {existingOfferings.length > 0 && (
                  <span className="bg-green-600 px-2 py-1 rounded mr-2">
                    {existingOfferings.length} Existing
                  </span>
                )}
                {addedPrograms.length > 0 && (
                  <span className="bg-blue-600 px-2 py-1 rounded">
                    {addedPrograms.length} New
                  </span>
                )}
                {selectedOfferings.length > 0 && (
                  <span className="bg-yellow-600 px-2 py-1 rounded ml-2">
                    {selectedOfferings.length} Selected
                  </span>
                )}
              </div>
              
              {(existingOfferings.length > 0 || addedPrograms.length > 0) && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1 bg-white text-blue-600 text-sm font-semibold rounded hover:bg-gray-100 transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleDeselectAll}
                    className="px-3 py-1 bg-white text-blue-600 text-sm font-semibold rounded hover:bg-gray-100 transition-colors"
                  >
                    Deselect All
                  </button>
                  
                  {selectedOfferings.length > 0 && (
                    <button
                      onClick={handleOpenForNominations}
                      disabled={openingForNominations}
                      className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                    >
                      {openingForNominations ? "Opening..." : "Open for Nominations"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {loadingExistingOfferings && (
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-center space-x-2 text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span className="text-sm">Loading existing offerings...</span>
              </div>
            </div>
          )}

          <div className="divide-y divide-gray-200">
            {existingOfferings.map((program) => (
              <div key={program.id} className={`${program.openForNomination ? 'bg-green-50 border-l-4 border-green-500' : 'bg-white'}`}>
                <div className={`px-6 py-4 flex items-center justify-between ${program.openForNomination ? 'hover:bg-green-100' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedOfferings.includes(program.id)}
                      onChange={(e) => handleOfferingSelection(program.id, e.target.checked)}
                      disabled={program.status === 'FINALIZED' || program.openForNomination}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={program.status === 'FINALIZED' ? 'Cannot open nominations for finalized programs' : program.openForNomination ? 'Nominations are already opened for this program' : 'Select to open for nominations'}
                    />
                    <button
                      onClick={() => toggleProgramExpansion(program.id)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      <svg
                        className={`w-5 h-5 transform transition-transform ${
                          expandedPrograms[program.id] ? "rotate-90" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                        <span>{program.blendedProgramName}</span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Existing</span>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Instance #{program.blendedProgramId || program.id.split('-')[1] || 'N/A'}</span>
                        {program.status === 'FINALIZED' ? (
                          <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded">✓ FINALIZED</span>
                        ) : (
                          program.openForNomination ? 
                            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">Nominations Opened</span> :
                            <span className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">Yet to Open For Nominations</span>
                        )}
                        {program.isEditing && <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Editing</span>}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Code: {program.programWDCourseCode || 'N/A'} | Track: {program.track || 'N/A'} | {program.modules.length} modules | ID: {program.blendedProgramId || 'N/A'}
                      </p>
                      {program.publishedDate && (
                        <p className="text-xs text-gray-500">
                          Published on: {program.publishedDate} by {program.publishedBy || 'N/A'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditProgram(program.id)}
                      className={`px-3 py-1 text-sm font-semibold rounded transition-colors ${
                        program.isEditing
                          ? "bg-yellow-500 text-white hover:bg-yellow-600"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      {program.isEditing ? "Cancel" : "Edit"}
                    </button>
                    <button
                      onClick={() => handleSaveProgram(program.id)}
                      disabled={loading || !program.isEditing || savingPrograms.has(program.id)}
                      className={`px-3 py-1 text-sm font-semibold rounded transition-colors ${
                        savingPrograms.has(program.id)
                          ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                          : 'bg-green-500 text-white hover:bg-green-600 disabled:bg-gray-400'
                      }`}
                    >
                      {savingPrograms.has(program.id) ? (
                        <>
                          <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></span>
                          Saving...
                        </>
                      ) : (
                        loading ? "Saving..." : "Save"
                      )}
                    </button>
                    <button
                      onClick={() => handleRemoveProgram(program.id)}
                      disabled={loading}
                      className="px-3 py-1 bg-red-500 text-white text-sm font-semibold rounded hover:bg-red-600 disabled:bg-gray-400 transition-colors"
                    >
                      {loading ? "Deleting..." : "Remove"}
                    </button>
                  </div>
                </div>
                {expandedPrograms[program.id] && renderProgramTable(program)}
              </div>
            ))}

            {addedPrograms.map((program) => (
              <div key={program.id} className={`${program.openForNomination ? 'bg-green-50 border-l-4 border-green-500' : 'bg-white'}`}>
                <div className={`px-6 py-4 flex items-center justify-between ${program.openForNomination ? 'hover:bg-green-100' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedOfferings.includes(program.id)}
                      onChange={(e) => handleOfferingSelection(program.id, e.target.checked)}
                      disabled={true}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="New programs must be saved first before they can be opened for nominations"
                    />
                    <button
                      onClick={() => toggleProgramExpansion(program.id)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      <svg
                        className={`w-5 h-5 transform transition-transform ${
                          expandedPrograms[program.id] ? "rotate-90" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div>
                      <h4 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                        <span>{program.blendedProgramName}</span>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">New</span>
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Instance #{program.id}</span>
                        {program.status === 'FINALIZED' ? (
                          <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded">✓ FINALIZED</span>
                        ) : (
                          program.openForNomination ? 
                            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">Nominations Opened</span> :
                            <span className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">Yet to Open For Nominations</span>
                        )}
                        {program.isEditing && <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Editing</span>}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Code: {program.programWDCourseCode || 'N/A'} | Track: {program.track || user.track || 'N/A'} | {program.modules.length} modules
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditProgram(program.id)}
                      className={`px-3 py-1 text-sm font-semibold rounded transition-colors ${
                        program.isEditing
                          ? "bg-yellow-500 text-white hover:bg-yellow-600"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      {program.isEditing ? "Cancel" : "Edit"}
                    </button>
                    <button
                      onClick={() => handleSaveProgram(program.id)}
                      disabled={loading || !program.isEditing || savingPrograms.has(program.id)}
                      className={`px-3 py-1 text-sm font-semibold rounded transition-colors ${
                        savingPrograms.has(program.id)
                          ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                          : 'bg-green-500 text-white hover:bg-green-600 disabled:bg-gray-400'
                      }`}
                    >
                      {savingPrograms.has(program.id) ? (
                        <>
                          <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></span>
                          Saving...
                        </>
                      ) : (
                        loading ? "Saving..." : "Save"
                      )}
                    </button>
                    <button
                      onClick={() => handleRemoveProgram(program.id)}
                      className="px-3 py-1 bg-red-500 text-white text-sm font-semibold rounded hover:bg-red-600 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                {expandedPrograms[program.id] && renderProgramTable(program)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default BlendedLearningOfferings;